import { DINING_ERROR_CODES } from '../constants/diningErrorCodes';
import {
  DiningRoomI,
  IssueCodeError,
  OBSI,
  SlotThematicAvailabilityWithDate,
  ThematicAvailabilityResponse,
} from '@msc-b2c/interfaces/interfaces';
import { getDiningIssueCode } from './diningIssueCode';
import { normalizeTimeSlots } from './diningAvailabilityUtils';

/**
 * Dining availability policy helpers.
 *
 * Why this module exists:
 * - Keep decision rules (enable/disable, normalization, issue-code behavior)
 *   out of rendering hooks/components.
 * - Make backend-contract interpretation explicit in one place.
 * - Ensure all callers apply the same INT135/INT136 semantics.
 */

/**
 * Extracts a stable dining issue code from unknown errors.
 * Falls back to UNKNOWN to avoid undefined error branching.
 */
export const toIssueCode = (error: unknown): string => {
  return getDiningIssueCode(error) ?? DINING_ERROR_CODES.UNKNOWN;
};

/**
 * Creates an Error object enriched with issue_code, so callers can throw a
 * standard Error while still preserving API-specific error metadata.
 */
export const createIssueCodeError = (issueCode: string): Error & IssueCodeError => {
  const error = new Error(issueCode) as Error & IssueCodeError;
  error.issue_code = issueCode;
  return error;
};

/**
 * Global rule for reserve-now disabling after INT136 failures.
 *
 * Exceptions intentionally kept enabled:
 * - UNAVAILABLE_SELECTION: user can retry/change selection.
 * - PASSENGERS_REQUIRED: user can select passengers and retry.
 */
export const shouldDisableReserveNowFromThematicIssueCode = (issueCode: string): boolean => {
  if (
    issueCode === DINING_ERROR_CODES.UNAVAILABLE_SELECTION ||
    issueCode === DINING_ERROR_CODES.PASSENGERS_REQUIRED
  ) {
    return false;
  }

  return true;
};

/**
 * Determines reserve-now baseline availability from INT130/INT135 bootstrap data.
 *
 * Disable when:
 * - backend explicitly says not AVAILABLE,
 * - payload reports HTTP-like error code,
 * - no reservable time slots are present.
 */
export const shouldDisableReserveNowFromShopObs = (
  payload: OBSI | Record<string, unknown>
): boolean => {
  const availabilityStatus =
    typeof payload.availabilityStatus === 'string'
      ? payload.availabilityStatus.toUpperCase()
      : undefined;

  if (availabilityStatus && availabilityStatus !== 'AVAILABLE') {
    return true;
  }

  if ('code' in payload && typeof payload.code === 'number' && payload.code >= 400) {
    return true;
  }

  const diningRoomsForSlots: DiningRoomI[] = Array.isArray(payload.diningRooms)
    ? payload.diningRooms
    : [];
  const reservableSlotsCount = diningRoomsForSlots.reduce((total, room) => {
    const timeSlots = normalizeTimeSlots(room.timeSlots);
    return total + timeSlots.filter(Boolean).length;
  }, 0);

  return reservableSlotsCount === 0;
};

/**
 * Normalizes INT136 response into a flat room/date/slot structure.
 *
 * Contract notes:
 * - Slot date is nested under room.dates[] and is copied onto each slot.
 * - Returned slots are normalized to isReserved=false because the active rule
 *   is slot presence => available.
 * - In mock mode, if dates do not match itinerary, fallback to first itinerary
 *   date so UI test data remains renderable.
 */
export const normalizeThematicResponse = (
  response: ThematicAvailabilityResponse,
  itineraryDays: ReadonlyArray<{ date: string }>,
  shouldUseApiMocks: boolean
): Array<{ diningRoomCode: string | undefined; slot: SlotThematicAvailabilityWithDate }> => {
  const fallbackDate = itineraryDays[0]?.date;
  return (
    response.diningRooms?.flatMap((room) => {
      const roomSlots =
        room.dates?.flatMap((dateNode) =>
          (dateNode.times ?? []).map((slot) => ({
            ...slot,
            date: dateNode.date ?? '',
          }))
        ) ?? [];

      const hasMatchingItineraryDate = roomSlots.some((slot) =>
        itineraryDays.some((day) => day.date === slot.date)
      );

      const normalizedRoomSlots =
        shouldUseApiMocks && !hasMatchingItineraryDate && fallbackDate
          ? roomSlots.map((slot) => ({ ...slot, date: fallbackDate }))
          : roomSlots;

      return normalizedRoomSlots.map((slot) => ({
        diningRoomCode: room.code,
        slot: {
          ...slot,
          isReserved: false,
        },
      }));
    }) ?? []
  );
};

/**
 * Expands INT130/INT135 fallback times into date-specific slots for the
 * currently visible itinerary.
 *
 * This produces seed UI slots before INT136 resolves per-room availability.
 */
export const buildSlotsFromShopObs = ({
  itineraryDays,
  seedDiningRoomCode,
  shopObsResponse,
}: {
  itineraryDays: ReadonlyArray<{ date: string }>;
  seedDiningRoomCode?: string;
  shopObsResponse: OBSI;
}): SlotThematicAvailabilityWithDate[] => {
  if (itineraryDays.length === 0) {
    return [];
  }

  const normalizedDiningRooms: DiningRoomI[] = Array.isArray(shopObsResponse.diningRooms)
    ? shopObsResponse.diningRooms
    : shopObsResponse.diningRooms
    ? [shopObsResponse.diningRooms as DiningRoomI]
    : [];

  const matchingRooms =
    seedDiningRoomCode && normalizedDiningRooms.length > 0
      ? normalizedDiningRooms.filter((room) => room.code === seedDiningRoomCode)
      : normalizedDiningRooms;

  const uniqueTimeSlots = Array.from(
    new Set(
      (matchingRooms ?? []).flatMap((room) => normalizeTimeSlots(room.timeSlots)).filter(Boolean)
    )
  );

  if (uniqueTimeSlots.length === 0) {
    return [];
  }

  return itineraryDays.flatMap((day) =>
    uniqueTimeSlots.map((time) => ({
      date: day.date,
      time,
      isReserved: false,
    }))
  );
};
